/*
 * HeroVideoLayer — resilient full-bleed hero video.
 *
 * Why it is built this way:
 *  - The poster is ALWAYS rendered underneath the <video>, so a failed decode,
 *    a blocked autoplay or a slow network never leaves a visual hole.
 *  - Four encodes: 1080p and 720p, each as H.264 + VP9/WebM. The resolution is
 *    chosen once from the viewport width; a browser that cannot decode the MP4
 *    silently falls through to the WebM source. *  - play() is called explicitly and retried on loadeddata, tab focus and the
 *    first pointer interaction — Safari Low Power Mode rejects the initial
 *    autoplay attempt even for muted inline video.
 *  - prefers-reduced-motion does NOT hide the video. Windows' "show animations"
 *    switch sets that flag for a huge number of desktop users, which silently
 *    replaced the hero with a still image. WCAG 2.2.2 is satisfied instead by
 *    the explicit pause control that VideoHero renders (see `paused` prop).
 */
import { useEffect, useRef, useState } from "react";
import { HERO_VIDEO_1080, HERO_VIDEO_720, HERO_POSTER_URL } from "@/lib/site";

// Picked once, before the element loads: a full-bleed hero on a wide screen
// gets the crisp 1080p pair, everything else the lean 720p pair.
const pickSources = () => {
  const conn = navigator.connection;
  const frugal = conn?.saveData || /(^|-)[23]g$/.test(conn?.effectiveType || "");
  return window.innerWidth >= 1100 && !frugal ? HERO_VIDEO_1080 : HERO_VIDEO_720;
};

export const HeroVideoLayer = ({ alt, paused = false }) => {
  const videoRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [started, setStarted] = useState(false);
  const [src] = useState(pickSources);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || failed) return;
    if (paused) {
      el.pause();
      return;
    }
    const tryPlay = () => {
      el.play()?.catch?.(() => {});
    };
    tryPlay();
    el.addEventListener("loadeddata", tryPlay);
    window.addEventListener("pointerdown", tryPlay, { once: true });
    document.addEventListener("visibilitychange", tryPlay);
    return () => {
      el.removeEventListener("loadeddata", tryPlay);
      window.removeEventListener("pointerdown", tryPlay);
      document.removeEventListener("visibilitychange", tryPlay);
    };
  }, [paused, failed]);

  const handleError = (e) => {
    // Only a real media error counts; React funnels nested error events here too.
    const err = e.currentTarget?.error;
    if (!err) return;
    console.warn(`[hero-video] media error ${err.code}: ${err.message || "no message"}`);
    setFailed(true);
  };

  return (
    <>
      <img
        src={HERO_POSTER_URL}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="hero-poster"
      />
      {!failed && (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            started ? "opacity-100" : "opacity-0"
          }`}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_POSTER_URL}
          onPlaying={() => setStarted(true)}
          onError={handleError}
          data-testid="hero-video"
        >
          <source src={src.mp4} type="video/mp4" />
          <source src={src.webm} type="video/webm" />
        </video>
      )}
    </>
  );
};
